import { keepPreviousData, useQuery, type QueryKey } from '@tanstack/react-query'

import { useApiClient } from '@/api/ApiClientProvider'
import type { ApiClient, ApiClientKind } from '@/api/client'
import type { CompetitionDetailQuery, SalesGranularity } from '@/api/competitionDetail'
import type { AttributionModel, Envelope } from '@/api/types'
import type { GlobalFilters } from '@/lib/filters'
import { filtersKey } from '@/lib/useGlobalFilters'

/*
 * The page's requests, one per route. The main route (summary, schedule, sales,
 * customers, timeline) is the first paint; each heavy tab route (funnel, sources,
 * devices, advertising) is read by its own panel, which mounts only while its tab is
 * open, so a tab nobody opens is never asked for.
 */

export const competitionDetailKeys = {
  all: ['competition-detail'] as const,
  /** The route, the client, the competition, the filters, then the route's own parameters (granularity, model). */
  route: (route: string, kind: ApiClientKind, competitionId: string, filters: GlobalFilters, ...own: readonly unknown[]) =>
    [...competitionDetailKeys.all, route, kind, competitionId, filtersKey(filters), ...own] as const,
}

/** Where the filters sit in a `route` key: the one part a placeholder may differ in. */
const FILTERS_AT = competitionDetailKeys.all.length + 3

/** Two keys that differ in their filters at most: the same route, competition and parameters. */
function differOnlyInFilters(next: QueryKey, previous: QueryKey): boolean {
  return next.length === previous.length && next.every((part, index) => index === FILTERS_AT || part === previous[index])
}

/**
 * While new filters load, the previous figures stay on screen (`keepPreviousData`, marked
 * busy) — but only when the filters are all that changed. Another competition's figures,
 * or another model's under this one's label, never stand in: those show the skeleton.
 */
function placeholderFor(key: QueryKey) {
  return <T>(previous: T | undefined, query: { queryKey: QueryKey } | undefined) =>
    query && differOnlyInFilters(key, query.queryKey) ? keepPreviousData(previous) : undefined
}

/** One route's read for the competition and filters; `enabled: false` holds it. */
function useRoute<T>(
  route: string,
  competitionId: string,
  filters: GlobalFilters,
  own: readonly unknown[],
  read: (client: ApiClient, query: CompetitionDetailQuery) => Promise<Envelope<T>>,
  enabled = true,
) {
  const client = useApiClient()
  const queryKey = competitionDetailKeys.route(route, client.kind, competitionId, filters, ...own)
  return useQuery({
    queryKey,
    queryFn: () => read(client, { competitionId, filters }),
    placeholderData: placeholderFor(queryKey),
    enabled,
  })
}

/** The main route; `enabled: false` holds the read (the Sales tab's weekly series, until Weekly is chosen). */
export function useCompetitionDetail(
  competitionId: string,
  filters: GlobalFilters,
  granularity: SalesGranularity = 'day',
  { enabled = true }: { enabled?: boolean } = {},
) {
  return useRoute('main', competitionId, filters, [granularity], (client, query) => client.competitionDetail({ ...query, granularity }), enabled)
}

export function useCompetitionFunnel(competitionId: string, filters: GlobalFilters) {
  return useRoute('funnel', competitionId, filters, [], (client, query) => client.competitionFunnel(query))
}

export function useCompetitionSources(competitionId: string, filters: GlobalFilters, model: AttributionModel) {
  return useRoute('sources', competitionId, filters, [model], (client, query) => client.competitionSources({ ...query, model }))
}

export function useCompetitionDevices(competitionId: string, filters: GlobalFilters) {
  return useRoute('devices', competitionId, filters, [], (client, query) => client.competitionDevices(query))
}

export function useCompetitionAdvertising(competitionId: string, filters: GlobalFilters) {
  return useRoute('advertising', competitionId, filters, [], (client, query) => client.competitionAdvertising(query))
}
